import React, { useCallback, useEffect, useState } from 'react';
import Header from '../../components/Header';
import ImageWithFallback from '../../components/ImageWithFallback';
import MartyrSelect from '../../components/MartyrSelect';
import { mediaApi } from '../../services/api';
import { toYouTubeEmbed } from '../../utils/youtube';

const emptyForm = {
  martyr_id: '',
  title: '',
  description: '',
  youtube_url: ''
};

const IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,image/*';
const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime,video/x-matroska,.mp4,.webm,.mov,.mkv';
const MAX_IMAGES = 40;
const MAX_VIDEOS = 20;
const MAX_FILE_BYTES = 80 * 1024 * 1024;
const IMAGE_BATCH_BYTES = 8 * 1024 * 1024;

const formatSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} ك.ب`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} م.ب`;
};

const chunkFilesBySize = (files, maxBytes) => {
  const chunks = [];
  let current = [];
  let size = 0;
  files.forEach((file) => {
    if (current.length && size + file.size > maxBytes) {
      chunks.push(current);
      current = [];
      size = 0;
    }
    current.push(file);
    size += file.size;
  });
  if (current.length) chunks.push(current);
  return chunks;
};

const mergeFiles = (current, incoming, max) => {
  const next = [...current];
  Array.from(incoming || []).forEach((file) => {
    const exists = next.some((item) => item.name === file.name && item.size === file.size && item.lastModified === file.lastModified);
    if (!exists) next.push(file);
  });
  return next.slice(0, max);
};

const FileThumb = ({ file }) => {
  const [src, setSrc] = useState('');
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  if (!src) return <div className="mb-1 h-20 rounded bg-stone-200 dark:bg-stone-700" />;
  return <img src={src} alt={file.name} className="mb-1 h-20 w-full rounded object-cover" />;
};

const FileDrop = ({ label, hint, accept, files, onAdd, onRemove, onClear, multipleMax, matchFile }) => {
  const [dragOver, setDragOver] = useState(false);

  const takeFiles = (list) => {
    const matched = Array.from(list || []).filter(matchFile);
    if (matched.length) onAdd(matched);
  };

  return (
    <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4 dark:border-stone-700 dark:bg-stone-900">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <p className="font-semibold">{label}</p>
          <p className="text-sm text-stone-500">{hint}</p>
        </div>
        {files.length > 0 && (
          <button type="button" className="text-sm text-error-600" onClick={onClear}>مسح الكل</button>
        )}
      </div>
      <label
        className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-8 text-center transition ${
          dragOver ? 'border-gold-500 bg-gold-500/10' : 'border-stone-300 dark:border-stone-600'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          takeFiles(e.dataTransfer.files);
        }}
      >
        <input
          type="file"
          multiple
          accept={accept}
          className="hidden"
          onChange={(e) => {
            takeFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <span className="text-sm font-medium">اسحب الملفات هنا أو اضغط للاختيار</span>
        <span className="mt-1 text-xs text-stone-500">يمكن تحديد مجموعة كاملة من نافذة الملفات (حتى {multipleMax})</span>
      </label>
      {files.length > 0 && (
        <ul className="mt-4 grid max-h-64 grid-cols-2 gap-2 overflow-y-auto md:grid-cols-3">
          {files.map((file, index) => (
            <li key={`${file.name}-${file.lastModified}-${index}`} className="relative overflow-hidden rounded-lg bg-white p-2 text-xs shadow-sm dark:bg-stone-800">
              {file.type.startsWith('image/') ? (
                <FileThumb file={file} />
              ) : (
                <div className="mb-1 flex h-20 items-center justify-center rounded bg-stone-200 text-2xl dark:bg-stone-700">▶</div>
              )}
              <p className="truncate font-medium" title={file.name}>{file.name}</p>
              <p className="text-stone-500">{formatSize(file.size)}</p>
              <button
                type="button"
                className="absolute start-1 top-1 rounded bg-black/70 px-1.5 py-0.5 text-white"
                onClick={() => onRemove(index)}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {files.length > 0 && <p className="mt-2 text-sm text-primary-800">تم اختيار {files.length} ملف</p>}
    </div>
  );
};

const AdminMediaPage = () => {
  const token = localStorage.getItem('adminToken');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [imageFiles, setImageFiles] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [filterType, setFilterType] = useState('');
  const [progress, setProgress] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const data = await mediaApi.getAll({
        limit: 48,
        ...(filterType && { media_type: filterType })
      }, token);
      setItems(data.media || []);
    } catch (err) {
      setError(err.message || 'تعذر تحميل الوسائط');
    } finally {
      setLoading(false);
    }
  }, [token, filterType]);

  useEffect(() => {
    if (!token) {
      window.location.href = '/admin/login';
      return;
    }
    fetchItems();
  }, [token, fetchItems]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!form.martyr_id) {
      setError('اختر اسم الشهيد أولاً');
      return;
    }
    if (imageFiles.length === 0 && videoFiles.length === 0 && !form.youtube_url.trim()) {
      setError('اختر مجموعة صور أو مجموعة فيديوهات أو أدخل رابط يوتيوب');
      return;
    }

    const oversized = [...imageFiles, ...videoFiles].find((file) => file.size > MAX_FILE_BYTES);
    if (oversized) {
      setError(`الملف «${oversized.name}» أكبر من 80 ميغابايت`);
      return;
    }

    const jobs = [];
    if (form.youtube_url.trim()) jobs.push({ kind: 'youtube' });
    chunkFilesBySize(imageFiles, IMAGE_BATCH_BYTES).forEach((files) => jobs.push({ kind: 'files', files }));
    videoFiles.forEach((file) => jobs.push({ kind: 'files', files: [file] }));

    setSaving(true);
    setProgress({ current: 0, total: jobs.length, percent: 0, label: 'بدء الرفع...' });
    try {
      let uploadedCount = 0;
      for (let index = 0; index < jobs.length; index += 1) {
        const job = jobs[index];
        const jobLabel = job.kind === 'youtube'
          ? 'رابط يوتيوب'
          : job.files.length === 1
            ? job.files[0].name
            : `${job.files.length} صور`;
        setProgress({
          current: index + 1,
          total: jobs.length,
          percent: 0,
          label: `جاري رفع ${index + 1} من ${jobs.length}: ${jobLabel}`
        });

        const formData = new FormData();
        formData.append('martyr_id', form.martyr_id);
        if (form.title.trim()) formData.append('title', form.title.trim());
        formData.append('description', form.description);
        if (job.kind === 'youtube') {
          formData.append('youtube_url', form.youtube_url.trim());
        } else {
          job.files.forEach((file) => formData.append('files', file));
        }

        const data = await mediaApi.create(formData, token, (percent) => {
          setProgress((prev) => ({ ...(prev || {}), percent }));
        });
        uploadedCount += (data.media || []).length;
      }

      setSuccess(`تم رفع ${uploadedCount} عنصراً بنجاح`);
      setForm(emptyForm);
      setImageFiles([]);
      setVideoFiles([]);
      fetchItems();
    } catch (err) {
      setError(err.message || 'تعذر رفع الوسائط');
    } finally {
      setSaving(false);
      setProgress(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل تريد حذف هذا الملف؟')) return;
    try {
      await mediaApi.delete(id, token);
      fetchItems();
    } catch (err) {
      setError(err.message || 'تعذر الحذف');
    }
  };

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      <Header />
      <main className="section-padding">
        <div className="container-responsive max-w-6xl">
          <h1 className="text-responsive-3xl font-bold text-primary-800 mb-2">الصور والفيديو</h1>
          <p className="text-gray-600 mb-8">ارفع الصور على دفعات، والفيديو يُرفع ملفاً واحداً تلو الآخر مع شريط التقدم. للسرعة استخدم رابط يوتيوب بدل رفع ملف فيديو كبير.</p>

          {error && <div className="mb-4 p-4 rounded-lg bg-red-50 text-red-700">{error}</div>}
          {success && <div className="mb-4 p-4 rounded-lg bg-green-50 text-green-700">{success}</div>}

          <div className="card mb-8">
            <div className="card-body">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="form-group">
                  <label className="form-label">اسم الشهيد</label>
                  <MartyrSelect
                    value={form.martyr_id}
                    onChange={(value) => setForm((prev) => ({ ...prev, martyr_id: value }))}
                    token={token}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="form-label">عنوان المجموعة (اختياري)</label>
                    <input
                      className="form-input"
                      value={form.title}
                      onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                      placeholder="إن تُرك فارغاً يُستخدم اسم كل ملف"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">رابط يوتيوب (اختياري)</label>
                    <input
                      className="form-input"
                      type="url"
                      value={form.youtube_url}
                      onChange={(e) => setForm((prev) => ({ ...prev, youtube_url: e.target.value }))}
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">وصف مختصر (اختياري)</label>
                  <textarea
                    className="form-input min-h-[80px]"
                    value={form.description}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  <FileDrop
                    label="مجموعة صور"
                    hint={`JPG / PNG / WEBP — حتى ${MAX_IMAGES} صورة`}
                    accept={IMAGE_ACCEPT}
                    files={imageFiles}
                    multipleMax={MAX_IMAGES}
                    matchFile={(file) => file.type.startsWith('image/')}
                    onAdd={(list) => setImageFiles((prev) => mergeFiles(prev, list, MAX_IMAGES))}
                    onRemove={(index) => setImageFiles((prev) => prev.filter((_, i) => i !== index))}
                    onClear={() => setImageFiles([])}
                  />
                  <FileDrop
                    label="مجموعة فيديوهات"
                    hint="MP4 / WEBM / MOV — يُرفع كل فيديو على حدة. يفضّل يوتيوب لأنه أسرع"
                    accept={VIDEO_ACCEPT}
                    files={videoFiles}
                    multipleMax={MAX_VIDEOS}
                    matchFile={(file) => file.type.startsWith('video/') || /\.(mp4|webm|mov|mkv)$/i.test(file.name)}
                    onAdd={(list) => setVideoFiles((prev) => mergeFiles(prev, list, MAX_VIDEOS))}
                    onRemove={(index) => setVideoFiles((prev) => prev.filter((_, i) => i !== index))}
                    onClear={() => setVideoFiles([])}
                  />
                </div>
                {progress && (
                  <div className="rounded-xl bg-stone-100 p-4 dark:bg-stone-800">
                    <p className="mb-2 text-sm font-medium">{progress.label} — {progress.percent}٪</p>
                    <div className="h-2 overflow-hidden rounded-full bg-stone-300 dark:bg-stone-700">
                      <div
                        className="h-full bg-gold-600 transition-all"
                        style={{ width: `${Math.max(progress.percent, 3)}%` }}
                      />
                    </div>
                  </div>
                )}
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving
                    ? (progress ? `جاري الرفع ${progress.current}/${progress.total}` : 'جاري الرفع...')
                    : `رفع ${imageFiles.length + videoFiles.length || ''} ${imageFiles.length + videoFiles.length ? 'ملف' : 'الوسائط'}`}
                </button>
              </form>
            </div>
          </div>

          <div className="flex gap-3 mb-6">
            <button className={`btn btn-sm ${filterType === '' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilterType('')}>الكل</button>
            <button className={`btn btn-sm ${filterType === 'image' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilterType('image')}>الصور</button>
            <button className={`btn btn-sm ${filterType === 'video' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setFilterType('video')}>الفيديو</button>
          </div>

          {loading ? (
            <p>جاري التحميل...</p>
          ) : items.length === 0 ? (
            <p className="text-gray-500">لا توجد وسائط بعد</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item) => (
                <div key={item.id} className="card">
                  <div className="h-48 bg-black">
                    {item.media_type === 'image' && item.file_url ? (
                      <ImageWithFallback src={item.file_url} alt={item.title} className="w-full h-full object-cover" />
                    ) : item.youtube_url ? (
                      <iframe
                        title={item.title}
                        src={toYouTubeEmbed(item.youtube_url)}
                        className="w-full h-full"
                        allowFullScreen
                      />
                    ) : item.file_url ? (
                      <video src={item.file_url} className="w-full h-full object-cover" controls />
                    ) : null}
                  </div>
                  <div className="card-body">
                    <h3 className="font-bold">{item.title}</h3>
                    <p className="text-sm text-primary-700">{item.martyr_name_ar}</p>
                    <button className="btn btn-error btn-sm mt-3" onClick={() => handleDelete(item.id)}>حذف</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminMediaPage;
